import { useState, useEffect, Dispatch, SetStateAction } from "react";
import { motion, AnimatePresence } from "framer-motion";

import { Button } from "@client:common/button";
import { IconButton } from "@client:common/button/icon-button";
import { Filter, Group } from "@client:common/filtering";
import FilterDropdown from "@client:common/filterDropdown/filterDropdown";
import FilterChips from "@client:common/filterChips/filterChips";

import FilterSection from "../filter-section/FilterSection";

import {
  Institutions,
  Category,
  EnergySector,
  Availability
} from "./filterConstants";

import "./FilterBar.light.scss";
import "./FilterBar.dark.scss";

interface Props {
  filters: Filter[];
  setFilters: Dispatch<SetStateAction<Filter[]>>;
}

type FilterConstant = Array<{ id: number; value: string }>;

export type BaseFilter = {
  selected: FilterConstant | undefined;
  selectAll: boolean;
};

const FilterBar = (props: Props) => {
  const { filters, setFilters } = props;

  const emptyFilter: BaseFilter = {
    selected: undefined,
    selectAll: false
  };

  const [category, setCategory] = useState<BaseFilter>(emptyFilter);
  const [institute, setInstitute] = useState<BaseFilter>(emptyFilter);
  const [energySector, setEnergySector] = useState<BaseFilter>(emptyFilter);
  const [availability, setAvailability] = useState<BaseFilter>(emptyFilter);
  const [search, setSearch] = useState<BaseFilter>(emptyFilter);
  const [isMobile, setIsMobile] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  function filterExists(name: string | number, group: Group) {
    return (
      filters.find((f) => f.name === name && f.group === group) !== undefined
    );
  }

  function addFilter(name: string | number, group: Group) {
    setFilters((currentFilters) => [...currentFilters, { name, group }]);
  }

  function removeFilter(name: string, group: Group) {
    setFilters((currentFilters) =>
      currentFilters.filter((f) => !(f.name === name && f.group === group))
    );
  }

  function resetFilters() {
    setFilters([]);
    setCategory(emptyFilter);
    setInstitute(emptyFilter);
    setEnergySector(emptyFilter);
    setAvailability(emptyFilter);
    setSearch(emptyFilter);
  }

  function toggleFilter(selectedValues: any, group: Group) {
    selectedValues?.forEach((filter: any) => {
      if (!filterExists(filter.value, group)) {
        addFilter.apply(null, [filter.value, group]);
      }
    });

    filters
      .filter((item) => item.group === group)
      .forEach((element: any) => {
        if (
          !selectedValues?.find((value: any) => value.value === element.name)
        ) {
          removeFilter.apply(null, [element.name, group]);
        }
      });
  }

  const updateFilterBarVisibility = () => {
    const screenWidth = window.innerWidth;
    const updatedIsMobile = screenWidth < 768;
    setIsMobile(updatedIsMobile);
    if (!updatedIsMobile) {
      setMobileFiltersOpen(false);
    }
  };

  useEffect(() => {
    updateFilterBarVisibility();

    window.addEventListener("resize", updateFilterBarVisibility);

    return () => {
      window.removeEventListener("resize", updateFilterBarVisibility);
    };
  }, []);

  const totalFiltersCount =
    (category.selected?.length || 0) +
    (institute.selected?.length || 0) +
    (energySector.selected?.length || 0) +
    (availability.selected?.length || 0);

  return (
    <div className="filters-chips-wrapper">
      <div className="filters-container">
        {isMobile ? (
          <>
            <div className="mobile-filters-header">
              <div className="filters-search_wrapper">
                <span className="material-icons">search</span>
                <input
                  className="filter-search"
                  type="search"
                  name="search"
                  placeholder="Search"
                  value={search.selected ? search?.selected[0].value : ""}
                  onChange={(e: any) => {
                    toggleFilter(
                      [{ id: 0, value: e.target.value }],
                      Group.SEARCH
                    );
                    setSearch({
                      ...search,
                      selected: [
                        { id: e.target.value.length, value: e.target.value }
                      ]
                    });
                  }}
                />
              </div>
              <IconButton
                style="stroked"
                color="primary"
                onClick={() => setMobileFiltersOpen(true)}
              >
                <span className="material-icons">filter_alt</span>
                {totalFiltersCount > 0 && (
                  <span className="filter-count">({totalFiltersCount})</span>
                )}
              </IconButton>
            </div>
          </>
        ) : (
          <>
            <div className="filters-search_wrapper">
              <p className="header__title--bold"></p>
              <span className="material-icons">search</span>
              <input
                className="filter-search"
                type="search"
                name="search"
                placeholder="Search"
                value={search.selected ? search?.selected[0].value : ""}
                onChange={(e: any) => {
                  toggleFilter(
                    [{ id: 0, value: e.target.value }],
                    Group.SEARCH
                  );
                  setSearch({
                    ...search,
                    selected: [
                      { id: e.target.value.length, value: e.target.value }
                    ]
                  });
                }}
              />
            </div>

            <FilterDropdown
              title="Category"
              placeholder="All Categories"
              filters={Category}
              selected={category}
              setSelected={setCategory}
              applyFilter={toggleFilter}
              group={Group.CATEGORY}
            />

            <FilterDropdown
              title="Institution"
              placeholder="All Institutions"
              filters={Institutions}
              selected={institute}
              setSelected={setInstitute}
              applyFilter={toggleFilter}
              group={Group.INSTITUTION}
            />

            <FilterDropdown
              title="Energy Sector"
              placeholder="All Energy Sectors"
              filters={EnergySector}
              selected={energySector}
              setSelected={setEnergySector}
              applyFilter={toggleFilter}
              group={Group.ENERGYSECTOR}
            />

            <FilterDropdown
              title="Availability"
              placeholder="All Availabilities"
              filters={Availability}
              selected={availability}
              setSelected={setAvailability}
              applyFilter={toggleFilter}
              group={Group.AVAILABILITY}
            />
            {!isMobile && (
              <div className="reset-button-container">
                <div className="reset-button-title"></div>
                <Button
                  style="basic"
                  color="primary"
                  onClick={() => resetFilters()}
                  disabled={filters.length === 0}
                >
                  Reset
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      <AnimatePresence>
        {mobileFiltersOpen && (
          <>
            <motion.div
              className="mobile-filter-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMobileFiltersOpen(false)}
            />
            <motion.div
              className="mobile-filter-panel"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "tween", duration: 0.3 }}
              drag="x"
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={0.2}
              onDragEnd={(_, info) => {
                if (info.offset.x > 100) {
                  setMobileFiltersOpen(false);
                }
              }}
              dragDirectionLock
              dragPropagation
            >
              <div className="mobile-filter-header">
                <h2>Filters</h2>
                <IconButton
                  style="basic"
                  color="primary"
                  onClick={() => setMobileFiltersOpen(false)}
                >
                  <span className="material-icons">close</span>
                </IconButton>
              </div>

              <div className="mobile-filter-sections">
                <FilterSection
                  title="Category"
                  filters={Category}
                  selected={category}
                  setSelected={setCategory}
                  applyFilter={toggleFilter}
                  group={Group.CATEGORY}
                />
                <FilterSection
                  title="Institution"
                  filters={Institutions}
                  selected={institute}
                  setSelected={setInstitute}
                  applyFilter={toggleFilter}
                  group={Group.INSTITUTION}
                />
                <FilterSection
                  title="Energy Sector"
                  filters={EnergySector}
                  selected={energySector}
                  setSelected={setEnergySector}
                  applyFilter={toggleFilter}
                  group={Group.ENERGYSECTOR}
                />
                <FilterSection
                  title="Availability"
                  filters={Availability}
                  selected={availability}
                  setSelected={setAvailability}
                  applyFilter={toggleFilter}
                  group={Group.AVAILABILITY}
                />
              </div>

              <div className="mobile-filter-actions">
                <Button
                  style="stroked"
                  color="primary"
                  onClick={() => {
                    resetFilters();
                  }}
                  className="clear-button"
                >
                  Clear All
                </Button>
                <Button
                  style="raised"
                  color="primary"
                  onClick={() => setMobileFiltersOpen(false)}
                  className="apply-button"
                >
                  Apply Filters{totalFiltersCount > 0 ? ` (${totalFiltersCount})` : ''}
                </Button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {filters && filters.length > 0 && (
        <div className="chips-container">
          {category.selected?.length > 0 &&
            category.selected.map((item) => (
              <FilterChips
                key={item.id}
                value={item}
                selected={category}
                setSelected={setCategory}
                applyFilter={toggleFilter}
                group={Group.CATEGORY}
              />
            ))}
          {institute.selected?.length > 0 && (
            <>
              {category.selected?.length && <p className="chips-value">AND</p>}{" "}
              {institute.selected.map((item) => (
                <FilterChips
                  key={item.id}
                  value={item}
                  selected={institute}
                  setSelected={setInstitute}
                  applyFilter={toggleFilter}
                  group={Group.INSTITUTION}
                />
              ))}
            </>
          )}
          {energySector.selected?.length > 0 && (
            <>
              {(institute.selected?.length || category.selected?.length) && (
                <p className="chips-value">AND</p>
              )}{" "}
              {energySector.selected.map((item) => (
                <FilterChips
                  key={item.id}
                  value={item}
                  selected={energySector}
                  setSelected={setEnergySector}
                  applyFilter={toggleFilter}
                  group={Group.ENERGYSECTOR}
                />
              ))}
            </>
          )}
          {availability.selected?.length > 0 && (
            <>
              {(institute.selected?.length ||
                category.selected?.length ||
                energySector.selected?.length) && (
                  <p className="chips-value">AND</p>
                )}{" "}
              {availability.selected.map((item) => (
                <FilterChips
                  key={item.id}
                  value={item}
                  selected={availability}
                  setSelected={setAvailability}
                  applyFilter={toggleFilter}
                  group={Group.AVAILABILITY}
                />
              ))}
            </>
          )}
          {search.selected && search.selected[0]?.value.length > 0 && (
            <>
              {(institute.selected?.length ||
                category.selected?.length ||
                energySector.selected?.length ||
                availability.selected?.length) && (
                  <p className="chips-value">AND</p>
                )}{" "}
              <FilterChips
                key={search?.selected[0].id}
                value={search.selected[0]}
                selected={search}
                setSelected={setSearch}
                applyFilter={toggleFilter}
                group={Group.SEARCH}
                quoted
              />{" "}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default FilterBar;
